import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Radio } from '@mantine/core'
import { RootState } from 'src/redux/store';
import { Button } from 'src/components/atoms';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { capitalizeFirstLetter } from "src/utils";
import useCleverTapEvents from 'src/events/useGetEvents';
import { BANK_DETAILS_EVENT } from 'src/events/constant';
import { ScreenWrapper, RadioSelect } from '../../components';
import { useStyles } from './linkBankAccount.styles';
import { OnboardingProgress } from "../../components/onboardingProgress";
import { getProgressStepper } from "../../utils/helpers";
import PennyDropFlow from './pennyDropFlow';
import ManualFlow from './manualFlow';
import { bankDetailsOptions } from '../../constants/bankDetails';

type DefaultScreenProps = {
  handleSelectFlow: (flow: string) => void;
};
const BADGE_VALUE = "Recommended"

const DefaultScreen = ({
  handleSelectFlow,
}: DefaultScreenProps) => {
  const { classes, cx } = useStyles();
  const { TriggerEvent } = useCleverTapEvents();
  const currentStep = useSelector(
    (state: RootState) => state.account.userDetails.currentStep
  );
  const [tempSelectedFlow, setTempSelectedFlow] = useState<string>('pennyDrop');
  const revPennyDropStatus = useSelector((state: RootState) => state.bankDetailsData.revPennyDropStatus.data)
  const recommendedFlow = revPennyDropStatus?.recommended
  const isRecommendedFlow = (key: string) => {
    if ((!recommendedFlow && key === 'rpd') || (recommendedFlow === key)) {
      return true;
    }
    return false;
  };

  // Set the active state of the radio button based on the recommended flow
  // TODO the current mapping is not clear, need to refactor
  // FE- pennyDrop -> BE- rpd
  // FE- manual -> BE- penny_drop
  useEffect(() => {
    switch (recommendedFlow) {
      case "rpd":
        setTempSelectedFlow("pennyDrop");
        break;
      case "penny_drop":
        setTempSelectedFlow("manual");
        break;
      default:
        setTempSelectedFlow("pennyDrop");
        break;
    }
  }, [revPennyDropStatus]);

  const selectFlow = () => {
    handleSelectFlow(tempSelectedFlow)
    TriggerEvent(
      BANK_DETAILS_EVENT.bankDetails,
      { bankVerificationMethod: tempSelectedFlow === "pennyDrop" ? "setu" : "manual" }
    )
  }

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">Link your Bank Account</h2>
          <p className="csDescription">Verify your bank account instantly via UPI or manually.</p>
          <Radio.Group
            name="bank account verification"
            value={tempSelectedFlow}
            onChange={(value) => setTempSelectedFlow(value)}
          >
            {bankDetailsOptions.map((item) => (
              <RadioSelect
                key={item.value}
                value={item.value}
                label={item.title}
                description={item.description}
                image={item.image}
                active={tempSelectedFlow === item.value}
                badgeValue={isRecommendedFlow(item.key) ? BADGE_VALUE : undefined}
              />
            ))}
          </Radio.Group>

        </div>
        <div className={cx("csFooter")}>
          <Button
            onClick={selectFlow}
          >
            Next
          </Button>
        </div>
      </div>
    </ScreenWrapper>
  );
};

export const LinkBankAccount = () => {
  const [userSelectedFlow, setUserSelectedFlow] = useState<string>('null')
  const bankDetailsState = useSelector((state: RootState) => state.bankDetailsData)

  const handleSelectFlow = (flow: string) => {
    setUserSelectedFlow(flow)
  }
  useEffect(() => {
    if (bankDetailsState.revPennyDrop.isError) {
      handleSelectFlow('null')
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(bankDetailsState.revPennyDrop.errMessage),
        icon: <IconError />,
      })
    }
  }, [bankDetailsState]);

  const displayContent = {
    pennyDrop: <PennyDropFlow handleSelectFlow={handleSelectFlow} />,
    manual: <ManualFlow handleSelectFlow={handleSelectFlow} />,
    null: <DefaultScreen handleSelectFlow={handleSelectFlow} />,
  }[userSelectedFlow];

  return displayContent as JSX.Element;
};

export default LinkBankAccount;
